import {
  Button,
  Divider,
  Group,
  NumberInput,
  Select,
  Stack,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useState } from 'react';
import { useLocation } from 'wouter';
import { JURISDICTION_LIST } from '@/lib/constants';
import { useCreateCompany } from '@/models/invoicingCompanies';
import { MONDAY_AR_LABELS, MONDAY_FINANCE_LABELS } from './mondayLabels';
import { listUrl } from '@/lib/listMemory';

export default function InvoicingCompanyCreate() {
  const [, nav] = useLocation();
  const createMut = useCreateCompany();
  const [form, setForm] = useState({
    name: '',
    countryCode: '',
    registrationNumber: '',
    vatNumber: '',
    legalAddress: '',
    vatRate: 0,
    priority: 100,
    website: '',
    mondayFinanceLabel: '',
    mondayArLabel: '',
  });

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const submit = () => {
    createMut.mutate(form as any, {
      onSuccess: (c) => nav(`/${c.id}`),
    });
  };

  return (
    <Stack gap="md" maw={720}>
      <Title order={3}>New Payer Company</Title>
      <Select
        label="Country"
        required
        searchable
        data={JURISDICTION_LIST.map(({ code, title }) => ({
          value: code,
          label: title,
        }))}
        value={form.countryCode}
        onChange={(v) => set('countryCode', v ?? '')}
      />
      <TextInput
        label="Name"
        required
        value={form.name}
        onChange={(e) => set('name', e.currentTarget.value)}
      />
      <Group grow>
        <TextInput
          label="Registration number"
          value={form.registrationNumber}
          onChange={(e) => set('registrationNumber', e.currentTarget.value)}
        />
        <TextInput
          label="VAT number"
          value={form.vatNumber}
          onChange={(e) => set('vatNumber', e.currentTarget.value)}
        />
      </Group>
      <Textarea
        label="Legal address"
        required
        autosize
        minRows={2}
        value={form.legalAddress}
        onChange={(e) => set('legalAddress', e.currentTarget.value)}
      />
      <Group grow>
        <NumberInput
          label="Default VAT rate"
          min={0}
          max={1}
          step={0.01}
          decimalScale={4}
          value={form.vatRate}
          onChange={(v) => set('vatRate', Number(v) || 0)}
        />
        <NumberInput
          label="Priority"
          min={1}
          value={form.priority}
          onChange={(v) => set('priority', Number(v) || 100)}
        />
      </Group>
      <TextInput
        label="Website"
        value={form.website}
        onChange={(e) => set('website', e.currentTarget.value)}
      />
      <Divider label="Monday.com" labelPosition="left" mt="xs" />
      <Group grow align="flex-start">
        <Select
          label="Finance board company"
          description='Column "Our Company Issuing Contract/Invoice"'
          placeholder="Leave the cell empty"
          data={[...MONDAY_FINANCE_LABELS]}
          clearable
          value={form.mondayFinanceLabel || null}
          onChange={(v) => set('mondayFinanceLabel', v ?? '')}
        />
        <Select
          label="Accounting Requests company"
          description='Subitem column "Status company name"'
          placeholder="Leave the cell empty"
          data={[...MONDAY_AR_LABELS]}
          clearable
          value={form.mondayArLabel || null}
          onChange={(v) => set('mondayArLabel', v ?? '')}
        />
      </Group>
      <Group justify="flex-end">
        <Button
          variant="subtle"
          onClick={() => nav(`~${listUrl('/invoicing-companies')}`)}
        >
          Cancel
        </Button>
        <Button
          loading={createMut.isPending}
          disabled={!form.name || !form.countryCode || !form.legalAddress}
          onClick={submit}
        >
          Create (inactive)
        </Button>
      </Group>
    </Stack>
  );
}
